import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Facebook, Instagram, YouTube, LinkedIn, WhatsApp, Email, Phone, KeyboardArrowUp } from '@mui/icons-material';
import XLogo from './XLogo';

const Footer: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const whatsappNumber = '263714574400';
  const whatsappLink = `https://wa.me/${whatsappNumber}`;

  return (
    <>
      <footer className="bg-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          {/* Logo on the left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <Link to="/" className="flex flex-col items-start mb-6">
              <img 
                src="/logo/New%20Bizani%20logo.png" 
                alt="Bizani Logo" 
                className="h-24 md:h-32 w-auto transition-all duration-300 mb-2 brightness-0 invert"
              />
            </Link>
          </motion.div>

          {/* About Us with Social Icons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-1 lg:ml-0"
          >
            <h3
              className="text-xl font-bold mb-4"
              style={{ fontFamily: "'Montserrat', 'Poppins', sans-serif" }}
            >
              About Us
            </h3>
              <p
                className="text-gray-300 leading-relaxed mb-6"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Bizani Media delivers impactful stories through innovative video production, branding, and digital solutions.
              </p>
              
              {/* Social Media Icons */}
              <div className="flex items-center gap-4 flex-wrap">
                <a
                  href="https://www.linkedin.com/company/bizanimedia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  <LinkedIn style={{ fontSize: '24px' }} />
                </a>
                <a
                  href="https://www.instagram.com/bizanimedia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="Instagram"
                >
                  <Instagram style={{ fontSize: '24px' }} />
                </a>
                <a
                  href="https://www.facebook.com/bizanimedia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="Facebook"
                >
                  <Facebook style={{ fontSize: '24px' }} />
                </a>
                <a
                  href="https://x.com/bizanimedia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="X"
                >
                  <XLogo size={24} />
                </a>
                <a
                  href="https://www.youtube.com/@bizanimedia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="YouTube"
                >
                  <YouTube style={{ fontSize: '24px' }} />
                </a>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="WhatsApp"
                >
                  <WhatsApp style={{ fontSize: '24px' }} />
                </a>
                <a
                  href="mailto:info@bizani.co.zw"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="Email"
                >
                  <Email style={{ fontSize: '24px' }} />
                </a>
                <a
                  href="tel:+263714574400"
                  className="text-white hover:text-bizani-orange transition-colors duration-200"
                  aria-label="Phone"
                >
                  <Phone style={{ fontSize: '24px' }} />
                </a>
              </div>
            </motion.div>

            {/* Quick Menu */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <h3
                className="text-xl font-bold mb-4"
                style={{ fontFamily: "'Montserrat', 'Poppins', sans-serif" }}
              >
                Quick Menu
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/terms"
                    className="text-gray-300 hover:text-bizani-orange transition-colors duration-200"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    T & Cs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-gray-300 hover:text-bizani-orange transition-colors duration-200"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-gray-300 hover:text-bizani-orange transition-colors duration-200"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    Get A Free Quote
                  </Link>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Bottom Section with Copyright */}
          <div className="mt-12 pt-8 text-center">
            <p
              className="text-gray-400 text-sm"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Bizani Media 2026 |{' '}
              <a
                href="https://www.spemedia.co.zw/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-bizani-orange transition-colors duration-200"
              >
                speMEDIA Site Design
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <motion.a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="WhatsApp"
      >
        <WhatsApp style={{ fontSize: '28px', color: 'white' }} />
      </motion.a>

      {/* Floating Back to Top Button */}
      {showBackToTop && (
        <motion.button
          onClick={scrollToTop}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-bizani-orange rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200"
          style={{ backgroundColor: '#EF3F23' }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Back to top"
        >
          <KeyboardArrowUp style={{ fontSize: '28px', color: 'white' }} />
        </motion.button>
      )}
    </>
  );
};

export default Footer;
